import { v4 as uuid } from "uuid";
import { createLog } from "./logs";

export type MapPosition = { lng: number; lat: number; zoom: number };

/**
 * Logs one opening of the map search.
 * Every log shares a session ID so the partial searches made while typing can be grouped together.
 */
export class MapSearchLogger {
    private readonly searchSessionId = uuid();

    private lastSearch: { searchString: string; resultCount: number } | null = null;

    private resultClicked = false;

    /** @param getMapPosition Called when each log is created, as the map can move while the search is open */
    constructor(private readonly getMapPosition: () => MapPosition) {}

    /** Logs what the user searched the map for and how many results were found */
    public onSearch = (searchString: string, resultGroups: { results: unknown[] }[]) => {
        const resultCount = resultGroups.reduce((count, group) => count + group.results.length, 0);
        this.lastSearch = { searchString, resultCount };
        this.resultClicked = false;
        createLog("Map search", "info", { ...this.getLogAttributes(), searchString, resultCount });
    };

    /** Logs which result the user clicked after searching the map */
    public onResultClicked = (searchString: string, resultType: string, result: { id: string }, displayText: string) => {
        this.resultClicked = true;
        createLog("Map search result clicked", "info", { ...this.getLogAttributes(), searchString, resultType, resultId: result.id, resultName: displayText });
    };

    /** Logs the last search when the user closes the search without clicking a result, so we can see what they couldn't find */
    public onClose = () => {
        if (this.lastSearch != null && !this.resultClicked) {
            createLog("Map search closed", "info", { ...this.getLogAttributes(), ...this.lastSearch });
        }
    };

    private getLogAttributes() {
        const { lng, lat, zoom } = this.getMapPosition();
        return { searchSessionId: this.searchSessionId, mapLng: lng, mapLat: lat, mapZoom: zoom };
    }
}
